import { DateTime } from 'luxon';

type FormatNotificationCreatedAtParams = {
  readonly createdAtIso: string;
  readonly now?: DateTime;
};

export function formatNotificationCreatedAt({ createdAtIso, now = DateTime.now() }: FormatNotificationCreatedAtParams) {
  const created = DateTime.fromISO(createdAtIso);
  if (!created.isValid) {
    return '';
  }

  const diffMs = now.toMillis() - created.toMillis();
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);

  // surface_at can be in the future on Active alerts (e.g. a task due later today
  // — update_alerts_from_task keeps status=Active when due_at <= tomorrow 00:00).
  // Fall through to the absolute-date branch instead of saying "Just now".
  if (diffMs < 0) {
    if (created.year === now.year) return created.toFormat('MMM d');
    return created.toFormat('MMM d, yyyy');
  }

  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const calendarDaysAgo = now.startOf('day').diff(created.startOf('day'), 'days').days;

  if (calendarDaysAgo === 1) {
    return 'Yesterday';
  }
  if (calendarDaysAgo >= 2 && calendarDaysAgo <= 6) {
    return created.toFormat('ccc');
  }
  if (created.year === now.year) {
    return created.toFormat('MMM d');
  }
  return created.toFormat('MMM d, yyyy');
}

type FormatNotificationDueDateParams = {
  readonly dueAtIso: string;
  readonly now?: DateTime;
};

export function formatNotificationDueDate({ dueAtIso, now = DateTime.utc() }: FormatNotificationDueDateParams) {
  const due = DateTime.fromISO(dueAtIso, { zone: 'utc' }).startOf('day');
  if (!due.isValid) {
    return null;
  }

  const today = now.startOf('day');
  const isOverdue = due < today;
  const dateLabel = due.year === today.year ? due.toFormat('MMM d') : due.toFormat('MMM d, yyyy');

  return {
    isOverdue,
    label: isOverdue ? `Overdue ${dateLabel}` : `Due ${dateLabel}`,
  };
}

// Relative-day form of a task due date for the Notifications v2 Inbox card:
// "Due in 3 days" for the future, "Due today" for today, "Overdue · 5 days"
// once past. Both dates are floored to the day so a partial day doesn't read as
// a whole one. Callers render the overdue label in red.
export function formatNotificationDueCountdown({ dueAtIso, now = DateTime.utc() }: FormatNotificationDueDateParams) {
  const due = DateTime.fromISO(dueAtIso, { zone: 'utc' }).startOf('day');
  if (!due.isValid) {
    return null;
  }

  const today = now.startOf('day');
  const days = Math.round(due.diff(today, 'days').days);

  if (days < 0) {
    const overdueBy = Math.abs(days);
    return { isOverdue: true, label: `Overdue · ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'}` };
  }
  if (days === 0) {
    return { isOverdue: false, label: 'Due today' };
  }
  return { isOverdue: false, label: `Due in ${days} ${days === 1 ? 'day' : 'days'}` };
}
